import { useQueries } from '@tanstack/react-query';
import { ChevronDown, Search, X } from 'lucide-react';
import * as React from 'react';
import { CheckSlot } from '@/components/custom/autocomplete';
import type { EntityPickerConfig } from '@/components/custom/entity-pickers';
import {
  type LoadStrategy,
  useEntityAutocomplete,
} from '@/components/custom/use-entity-autocomplete';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { popoverScrollFix } from '@/lib/popover-scroll-fix';
import { cn } from '@/lib/utils';

// Generic entity picker built on the button + in-popover-search model: a
// trigger that summarizes the selection, opening a popover with its own
// search box and a checkmark-gutter list. ONE component covers both
// single- and multi-select (mode chosen by the `multiple` prop) for any
// entity, supplied entirely through an `EntityPickerConfig` (see
// `entity-pickers`) — so customers, providers, locations, etc. share this
// widget instead of forking a near-identical autocomplete each.
//
// Data + lazy loading are delegated to `useEntityAutocomplete`:
//   - 'lazy' (default): debounced server `q=` search, one page at a time —
//     scales to catalogs with thousands of rows (customers, employees).
//   - 'preload': fetch one page on first open, filter in memory — for small
//     catalogs (providers, locations) where client-side filtering is snappier.
// Selected ids are resolved to labels via a `useQueries` fan-out over the
// config's `detailKey`/`fetchOne`, cache-shared with every other consumer of
// the same config, so a selection that isn't on the current search page (or
// whose entity was since deleted) still renders and stays removable.
//
// This is the filter/picker control. The type-into-field typeahead
// (`Autocomplete` / the single-select autocompletes) is a separate, deliberately
// faster control for inline data entry — not replaced by this.

interface EntityComboboxBaseProps<T> {
  config: EntityPickerConfig<T>;
  /** See `useEntityAutocomplete`. Defaults to 'lazy' (server search). */
  loadStrategy?: LoadStrategy;
  /** Page size for the one-shot 'preload' fetch. Ignored when lazy. */
  preloadLimit?: number;
  /** Per-page size for 'lazy'/'always' fetches. */
  lazyLimit?: number;
  /** Row body. Defaults to the entity's `getLabel`. The checkmark gutter is
   *  rendered by this component; `renderItem` fills the label area only. */
  renderItem?: (item: T, isSelected: boolean) => React.ReactNode;
  /** Placeholder for the in-popover search input. */
  searchPlaceholder?: string;
  /** Trigger text when nothing is selected. */
  placeholder?: string;
  /** Popover sizing.
   *  - `'anchor'` (default): fixed width, matching the original picker.
   *  - `'auto'`: use the trigger width as a min but let the popover grow to
   *    fit the widest row (up to a cap). Pick this when rows carry detail the
   *    trigger is too narrow for — e.g. an employee row's email line. */
  popoverWidth?: 'anchor' | 'auto';
  disabled?: boolean;
  className?: string;
  id?: string;
  /** Id of the visible label element. The trigger is a div, which a
   *  `<label htmlFor>` cannot name, so link the label through this. Without
   *  it the trigger is named by its placeholder, so it is never anonymous. */
  labelledBy?: string;
}

interface SingleProps<T> extends EntityComboboxBaseProps<T> {
  multiple?: false;
  value: string | null;
  onChange: (id: string | null, item: T | null) => void;
  /** Show the inline clear (×). Defaults true. */
  clearable?: boolean;
}

interface MultiProps<T> extends EntityComboboxBaseProps<T> {
  multiple: true;
  value: readonly string[];
  // ids only: the selected entities aren't always resolved client-side (a
  // chosen id may be off the current search page until its detail query
  // settles), so the component can't reliably hand back parallel item
  // objects. Callers that need the entities resolve them from the shared
  // cache via the same config. Single mode does return the item because its
  // selection is always the just-clicked row.
  onChange: (ids: string[]) => void;
  /** How the trigger handles many badges:
   *  - 'wrap' (default): badges wrap onto as many rows as needed.
   *  - 'scroll': badge area is capped and scrolls — keeps a filter bar's
   *    height stable when sat next to other controls. */
  badgeOverflow?: 'wrap' | 'scroll';
}

export type EntityComboboxProps<T> = SingleProps<T> | MultiProps<T>;

export function EntityCombobox<T>(props: EntityComboboxProps<T>) {
  const {
    config,
    loadStrategy = 'lazy',
    preloadLimit,
    lazyLimit,
    renderItem,
    searchPlaceholder = 'Search…',
    placeholder = 'All',
    popoverWidth = 'anchor',
    disabled,
    className,
    id,
    labelledBy,
  } = props;
  const { getId, getLabel, fetchList, fetchOne, listKey, detailKey } = config;
  const searchText = config.searchText ?? getLabel;

  const multiple = props.multiple === true;
  const selectedIds = React.useMemo(
    () => (multiple ? [...props.value] : props.value ? [props.value] : []),
    [multiple, props.value]
  );

  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listboxId = React.useId();

  // Single mode drives the hook's single-detail fetch with the current value;
  // multi resolves its set via the `useQueries` fan-out below, so it passes
  // `value: null` to keep the hook's single-detail query idle.
  const auto = useEntityAutocomplete<T>({
    value: multiple ? null : (props.value ?? null),
    strategy: loadStrategy,
    fetchList,
    fetchOne,
    listKey,
    detailKey,
    preloadLimit,
    lazyLimit,
    excludeCount: multiple ? selectedIds.length : 0,
    resolveSelected: multiple && selectedIds.length > 0,
  });

  // Resolve selected ids → items for the trigger badges (multi) and the pinned
  // rows. Preload holds the whole catalog in `auto.items`, so most labels
  // resolve from that list (see `byId`) with no per-id fetch. Fetch only the
  // ids NOT already in the list: the lazy/server path (no full list) and the
  // preload edge case where a selected id fell outside the capped preload page
  // (a catalog larger than `preloadLimit`) — otherwise its badge would render a
  // permanent "…".
  const resolvedIds = React.useMemo(
    () => new Set(auto.items.map((it) => getId(it))),
    [auto.items, getId]
  );
  const unresolvedIds = selectedIds.filter((sid) => !resolvedIds.has(sid));
  const details = useQueries({
    queries: unresolvedIds.map((sid) => ({
      queryKey: detailKey(sid),
      queryFn: () => fetchOne(sid),
      staleTime: 60_000,
    })),
  });

  const byId = React.useMemo(() => {
    const map = new Map<string, T>();
    for (const it of auto.items) map.set(getId(it), it);
    for (const d of details) {
      const item = d.data?.data;
      if (item) map.set(getId(item), item);
    }
    if (!multiple && auto.selectedItem) map.set(getId(auto.selectedItem), auto.selectedItem);
    return map;
    // `details`/`auto.items` identity changes each query settle — intended.
  }, [auto.items, auto.selectedItem, details, getId, multiple]);

  const selectedSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);

  const setSearch = (next: string) => {
    setQuery(next);
    auto.setQuery(next);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    auto.setOpen(next);
    if (!next) setSearch('');
  };

  const commitSingle = (item: T | null) => {
    (props as SingleProps<T>).onChange(item ? getId(item) : null, item);
  };
  const commitMulti = (ids: string[]) => {
    (props as MultiProps<T>).onChange(ids);
  };

  const toggle = (sid: string) => {
    if (multiple) {
      const next = new Set(selectedSet);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      commitMulti([...next]);
    } else {
      const item = byId.get(sid) ?? null;
      commitSingle(item);
      handleOpenChange(false);
    }
  };

  const remove = (sid: string) => commitMulti(selectedIds.filter((x) => x !== sid));

  const clearAll = () => {
    if (multiple) commitMulti([]);
    else commitSingle(null);
  };

  // Pinned selected rows first (always toggleable), then the search results.
  // Memoized: a fresh array every render would reconcile the whole listbox on
  // each keystroke / detail-query settle. Preload filters its full page in
  // memory (by `searchText`, so non-label fields like qbId/email match); lazy
  // hands back server results as-is. Either way drop already-selected ids —
  // they're pinned separately.
  const rows: { id: string; item: T | null }[] = React.useMemo(() => {
    const q = query.toLowerCase();
    const results = (
      auto.isClient && q
        ? auto.items.filter((it) => searchText(it).toLowerCase().includes(q))
        : auto.items
    ).filter((it) => !selectedSet.has(getId(it)));
    return [
      ...selectedIds.map((sid) => ({ id: sid, item: byId.get(sid) ?? null })),
      ...results.map((it) => ({ id: getId(it), item: it })),
    ];
  }, [auto.isClient, auto.items, query, selectedIds, selectedSet, byId, getId, searchText]);

  const singleClearable = !multiple && (props as SingleProps<T>).clearable !== false;
  const badgeOverflow = multiple ? ((props as MultiProps<T>).badgeOverflow ?? 'wrap') : 'wrap';

  // Resolved label for the single trigger; null → show the placeholder. A
  // selected-but-not-yet-resolved value reads as the placeholder (not a "…")
  // so a filtered page load doesn't flash a meaningless ellipsis.
  const singleSelected = !multiple && selectedIds[0] ? byId.get(selectedIds[0]) : undefined;
  const singleLabel = singleSelected ? getLabel(singleSelected) : null;

  return (
    <Popover open={open}>
      <PopoverAnchor asChild>
        <div ref={containerRef} className="relative w-full">
          {/* Trigger is a focusable div (role=combobox), not a <button>, so the
              clear and per-badge remove affordances can be real nested <button>s
              — a button inside a button is invalid HTML. Enter/Space toggle the
              popover, matching native select keyboard behaviour. */}
          <div
            id={id}
            role="combobox"
            aria-labelledby={labelledBy}
            aria-label={labelledBy ? undefined : placeholder}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-controls={open ? listboxId : undefined}
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : 0}
            onClick={() => !disabled && handleOpenChange(!open)}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleOpenChange(!open);
              }
            }}
            className={cn(
              'flex min-h-9 w-full cursor-pointer items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-left text-sm shadow-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
              className
            )}
          >
            {multiple ? (
              selectedIds.length === 0 ? (
                <span className="text-muted-foreground">{placeholder}</span>
              ) : (
                <span
                  className={cn(
                    'flex flex-1 flex-wrap items-center gap-1',
                    badgeOverflow === 'scroll' && 'max-h-16 overflow-y-auto'
                  )}
                >
                  {selectedIds.map((sid) => {
                    const item = byId.get(sid);
                    return (
                      <SelectionBadge
                        key={sid}
                        label={item ? getLabel(item) : '…'}
                        onRemove={() => remove(sid)}
                      />
                    );
                  })}
                </span>
              )
            ) : singleLabel ? (
              <span className="truncate">{singleLabel}</span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}

            <span className="flex shrink-0 items-center gap-1 text-muted-foreground/60">
              {singleClearable && selectedIds.length > 0 ? (
                <button
                  type="button"
                  aria-label="Clear selection"
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAll();
                  }}
                  className="rounded-sm p-0.5 transition-colors hover:text-foreground"
                >
                  <X size={14} />
                </button>
              ) : null}
              <ChevronDown
                size={16}
                className={cn('transition-transform', open && 'rotate-180')}
                aria-hidden
              />
            </span>
          </div>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={4}
        className={cn(
          'overflow-hidden p-0',
          // 'auto' grows to fit the widest row (min = trigger width) up to a
          // cap so an employee row's email line isn't clipped; 'anchor' keeps
          // the original fixed width. w-auto is required to override shadcn's
          // default w-72 base class (see Autocomplete for the same note).
          popoverWidth === 'auto' ? 'w-auto max-w-md' : 'w-72'
        )}
        style={
          popoverWidth === 'auto' ? { minWidth: 'var(--radix-popper-anchor-width)' } : undefined
        }
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          const target = e.target as Node | null;
          if (target && containerRef.current?.contains(target)) {
            e.preventDefault();
            return;
          }
          handleOpenChange(false);
        }}
        onEscapeKeyDown={() => handleOpenChange(false)}
      >
        <div className="flex items-center gap-2 border-b px-2.5">
          <Search className="size-4 shrink-0 text-muted-foreground/60" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-9 border-0 px-0 shadow-none focus-visible:ring-0"
          />
        </div>
        <div
          id={listboxId}
          role="listbox"
          aria-multiselectable={multiple || undefined}
          className="max-h-72 space-y-0.5 overflow-auto p-1"
          {...popoverScrollFix}
        >
          {auto.loading && rows.length === 0 ? (
            <div className="px-2 py-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="px-2 py-6 text-center text-sm text-muted-foreground">No matches</div>
          ) : (
            rows.map((row) => {
              const isSelected = selectedSet.has(row.id);
              return (
                <button
                  key={row.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                  onClick={() => toggle(row.id)}
                >
                  <CheckSlot isSelected={isSelected} />
                  <span className="min-w-0 flex-1">
                    {row.item ? (
                      renderItem ? (
                        renderItem(row.item, isSelected)
                      ) : (
                        getLabel(row.item)
                      )
                    ) : (
                      <span className="text-muted-foreground">Unknown ({row.id.slice(0, 8)}…)</span>
                    )}
                  </span>
                </button>
              );
            })
          )}
        </div>
        {selectedIds.length > 0 ? (
          <div className="border-t p-1">
            <button
              type="button"
              onClick={clearAll}
              className="w-full rounded-sm px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-accent"
            >
              {multiple ? `Clear ${selectedIds.length} selected` : 'Clear'}
            </button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

/** Removable selection chip shown in the multi-select trigger. Built on the
 *  design-system `Badge` so chip styling tracks the rest of the app; the remove
 *  affordance is a real `<button>` (legal now that the trigger is a div, not a
 *  button) that stops propagation so it doesn't toggle the popover. */
function SelectionBadge({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <Badge variant="secondary" className="gap-1 font-normal">
      <span className="max-w-40 truncate">{label}</span>
      <button
        type="button"
        aria-label={`Remove ${label}`}
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="rounded-sm hover:text-foreground"
      >
        <X size={12} />
      </button>
    </Badge>
  );
}
